import { getIn, useFormikContext } from "formik";
import React from "react";
import { Col, Row } from "react-bootstrap";

import {
	DisabledDependentNature,
	DisabledDependentRelation,
	MaximumLimits,
	PersonAgeOptions,
	PhysicalDisability,
	RegimeTypes
} from "../../../config/filingConstants";
import { IFormITR1 } from "../../../config/interfaces";
import { UtilService } from "../../../services";
import PriceDisplay from "../../PriceDisplay";
import { FormField, NumericInputField, RadioButtonGroup, SelectInputField } from "../../fields";

const OtherDeductionsDetailsForm: React.FC = () => {
	const { values, touched, errors, setFieldValue, handleBlur } = useFormikContext<IFormITR1>();

	const RegimeType = values?.FilingReturnInfo?.RegimeType || "";

	const handleTotalCalculation = (section: string, amount: number) => {
		const _Total = Object.entries(values.DeductUndChapVIA).reduce((curr, [key, value]) => {
			if (key === section) {
				return Number(curr) + Number(amount);
			}
			return Number(curr) + Number(value);
		}, 0);

		setFieldValue("TotalChapVIADeductions", _Total);
	};

	return (
		<>
			<section>
				<div className="section-title-wrap">
					<h5>80DD - Disabled Dependent Deduction</h5>
				</div>
				<Row className="align-items-center">
					<Col sm={6}>
						<SelectInputField
							name="OtherDeductions.DependentDisabled80DD.Nature"
							label="Disability Nature"
							isSearchable={true}
							isOptionArrayOfObject={true}
							isClearable={true}
							options={UtilService.convertToArray(DisabledDependentNature)}
							value={
								values?.OtherDeductions?.DependentDisabled80DD?.Nature
									? {
											"label":
												DisabledDependentNature[
													values?.OtherDeductions?.DependentDisabled80DD?.Nature
												],
											"value": values?.OtherDeductions?.DependentDisabled80DD?.Nature || ""
									  }
									: undefined
							}
							onBlur={handleBlur("OtherDeductions.DependentDisabled80DD.Nature")}
							onChange={(selectedCountry) => {
								if (selectedCountry) {
									if (selectedCountry.value === "FORTY_PERCENT_DISABILITY") {
										setFieldValue(
											"OtherDeductions.TotalSection80DD",
											MaximumLimits.Section80DD40Per
										);
										setFieldValue("DeductUndChapVIA.Section80DD", MaximumLimits.Section80DD40Per);
										handleTotalCalculation("Section80DD", MaximumLimits.Section80DD40Per);
									} else if (selectedCountry.value === "SEVERE_DISABILITY") {
										setFieldValue(
											"OtherDeductions.TotalSection80DD",
											MaximumLimits.Section80DD80Per
										);
										setFieldValue("DeductUndChapVIA.Section80DD", MaximumLimits.Section80DD80Per);
										handleTotalCalculation("Section80DD", MaximumLimits.Section80DD80Per);
									}
									setFieldValue(
										"OtherDeductions.DependentDisabled80DD.Nature",
										selectedCountry.value
									);
								} else {
									setFieldValue("OtherDeductions.DependentDisabled80DD.Nature", "");
									setFieldValue("OtherDeductions.TotalSection80DD", "");
									setFieldValue("DeductUndChapVIA.Section80DD", 0);
									handleTotalCalculation("Section80DD", 0);
								}
							}}
							isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
						/>
					</Col>
					<Col sm={6}>
						<SelectInputField
							name="OtherDeductions.DependentDisabled80DD.Relation"
							label="Dependent Type"
							isSearchable={true}
							isOptionArrayOfObject={true}
							isClearable={true}
							options={UtilService.convertToArray(DisabledDependentRelation)}
							value={
								values.OtherDeductions?.DependentDisabled80DD?.Relation
									? {
											"label":
												DisabledDependentRelation[
													values.OtherDeductions?.DependentDisabled80DD?.Relation
												],
											"value": values.OtherDeductions?.DependentDisabled80DD?.Relation || ""
									  }
									: undefined
							}
							onChange={(selectedState) => {
								if (selectedState) {
									setFieldValue(
										"OtherDeductions.DependentDisabled80DD.Relation",
										selectedState.value
									);
								} else {
									setFieldValue("OtherDeductions.DependentDisabled80DD.Relation", "");
								}
							}}
							onBlur={handleBlur("OtherDeductions.DependentDisabled80DD.Relation")}
							isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
						/>
					</Col>
				</Row>
			</section>
			<section className="mb-4">
				<div className="section-title-wrap">
					<h5>
						80U - Self Disability
						<small>Offers tax benefits if an individual suffers a disability.</small>
					</h5>
				</div>
				<Row className="align-items-center">
					<Col>
						<RadioButtonGroup
							name="OtherDeductions.SelfDisabilitySection80U"
							options={UtilService.convertToArray(PhysicalDisability)}
							value={values.OtherDeductions.SelfDisabilitySection80U}
							onChange={(selectedType) => {
								if (selectedType === "FORTY_PERCENT_DISABILITY") {
									setFieldValue("OtherDeductions.TotalSection80U", MaximumLimits.Section80U40Per);
									setFieldValue("DeductUndChapVIA.Section80U", MaximumLimits.Section80U40Per);
									handleTotalCalculation("Section80U", MaximumLimits.Section80U40Per);
								} else if (selectedType === "SEVERE_DISABILITY") {
									setFieldValue("OtherDeductions.TotalSection80U", MaximumLimits.Section80U80Per);
									setFieldValue("DeductUndChapVIA.Section80U", MaximumLimits.Section80U80Per);
									handleTotalCalculation("Section80U", MaximumLimits.Section80U80Per);
								} else {
									setFieldValue("OtherDeductions.TotalSection80U", "");
									setFieldValue("DeductUndChapVIA.Section80U", 0);
									handleTotalCalculation("Section80U", 0);
								}
								setFieldValue("OtherDeductions.SelfDisabilitySection80U", selectedType);
							}}
							containerClasses="w-100 flex-wrap"
							btnClasses="btn-w-160"
							isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
						/>
					</Col>
				</Row>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80EE - Interest on Home Loan (sanctioned between April 2016 and March 2017)
						<small>
							The house value should be less than Rs. 50 Lakhs and the total home loan should be
							less than Rs. 35 Lakhs.
						</small>
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80EE} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Interest on First-House Loan" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80EE"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80EE
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80EE")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80EE", value);
							setFieldValue("DeductUndChapVIA.Section80EE", Number(value));
							handleTotalCalculation("Section80EE", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80EE`)) &&
							getIn(touched, `OtherDeductions.TotalSection80EE`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80EE`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80EEA - Interest on Home Loan (sanctioned between April 2020 and March 2023)
						<small>The value of the house should be less than Rs. 45 Lakhs.</small>
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80EEA} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Interest on First-House Loan" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80EEA"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80EEA
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80EEA")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80EEA", value);
							setFieldValue("DeductUndChapVIA.Section80EEA", Number(value));
							handleTotalCalculation("Section80EEA", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80EEA`)) &&
							getIn(touched, `OtherDeductions.TotalSection80EEA`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80EEA`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80EEB - Electric Vehicle Loan
						<small>
							This loan should be sanctioned between April 1st, 2019 to March 31st 2023. Deduction
							can be claimed upto Rs. 1.5 lakhs.Deduction under section 80EEB is available on
							interest on loan taken from a financial institution for purchase of an electric
							vehicle.
						</small>
						<small>
							Max Limit of <PriceDisplay price={MaximumLimits.Section80EEB} decimalUpto={0} />
						</small>
					</h5>
				</div>
				<FormField label="Interest on loan taken for the purchase of an electric vehicle" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80EEB"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80EEB
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80EEB")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80EEB", value);
							setFieldValue("DeductUndChapVIA.Section80EEB", Number(value));
							handleTotalCalculation("Section80EEB", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80EEB`)) &&
							getIn(touched, `OtherDeductions.TotalSection80EEB`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80EEB`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80QQB - Royalty received on books
						<small>Deduction under this section can be claimed only against business income</small>
					</h5>
				</div>
				<FormField
					label="Royalty/copyright fee or lumpsum received by authors of
      literary/scientific/artistic books"
					sm={6}
				>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80QQB"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80QQB
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80QQB")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80QQB", value);
							setFieldValue("DeductUndChapVIA.Section80QQB", Number(value));
							handleTotalCalculation("Section80QQB", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80QQB`)) &&
							getIn(touched, `OtherDeductions.TotalSection80QQB`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80QQB`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80RRB - Income on Patents/Inventions
						<small>
							Royalty/transfer-of-rights/lump-sum consideration are eligible for deduction.
							Deduction under this section can be claimed only against business income
						</small>
					</h5>
				</div>
				<FormField label="Deduction amount for patentee" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80RRB"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80RRB
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80RRB")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80RRB", value);
							setFieldValue("DeductUndChapVIA.Section80RRB", Number(value));
							handleTotalCalculation("Section80RRB", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80RRB`)) &&
							getIn(touched, `OtherDeductions.TotalSection80RRB`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80RRB`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>80GGC - Contribution to political party</h5>
				</div>
				<FormField label="Contribution or donation made to a political party" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80GGC"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80GGC
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80GGC")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80GGC", value);
							setFieldValue("DeductUndChapVIA.Section80GGC", Number(value));
							handleTotalCalculation("Section80GGC", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80GGC`)) &&
							getIn(touched, `OtherDeductions.TotalSection80GGC`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80GGC`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80JJA - Bio-degradable waste business
						<small>Deduction under this section can be claimed only against business income</small>
					</h5>
				</div>
				<FormField
					label="Enter the deduction exempting profits from bio-degradable waste business"
					sm={6}
				>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80JJA"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80JJA
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80JJA")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80JJA", value);
							setFieldValue("DeductUndChapVIA.Section80JJA", Number(value));
							handleTotalCalculation("Section80JJA", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80JJA`)) &&
							getIn(touched, `OtherDeductions.TotalSection80JJA`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80JJA`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>
						80DDB - Deductions for treatment of specified diseases and ailments
						<small>
							This deduction is for certain specified diseases. (It is not a commonly applicable
							deduction). Please be careful while claiming this deduction.
						</small>
					</h5>
				</div>
				<FormField label="Age of person for whom deduction is claimed" sm={6}>
					<SelectInputField
						placeholder="Please Select Age of person"
						name="OtherDeductions.AgeOfPerson80DDB"
						menuZIndex={9}
						isOptionArrayOfObject={false}
						options={PersonAgeOptions}
						value={[values.OtherDeductions.AgeOfPerson80DDB || "Below 60 years"]}
						onChange={(selectedOption) => {
							if (selectedOption) {
								setFieldValue(`OtherDeductions.AgeOfPerson80DDB`, selectedOption.value);
							} else {
								setFieldValue(`OtherDeductions.AgeOfPerson80DDB`, undefined);
							}
						}}
						onBlur={handleBlur(`OtherDeductions.AgeOfPerson80DDB`)}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.AgeOfPerson80DDB`)) &&
							getIn(touched, `OtherDeductions.AgeOfPerson80DDB`)
						}
						errorMsg={getIn(errors, `OtherDeductions.AgeOfPerson80DDB`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
				<FormField
					label="Medical treatment costs for specified diseases under
      Section 80DDB"
					sm={6}
				>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80DDB"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80DDB
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80DDB")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80DDB", value);
							setFieldValue("DeductUndChapVIA.Section80DDB", Number(value));
							handleTotalCalculation("Section80DDB", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80DDB`)) &&
							getIn(touched, `OtherDeductions.TotalSection80DDB`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80DDB`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
			<section>
				<div className="section-title-wrap">
					<h5>
						80CCH - Contribution to Agnipath scheme
						<small>This deduction can only be claimed if employer is Central Government</small>
					</h5>
				</div>
				<FormField label="Contribution to Agnipath scheme" sm={6}>
					<NumericInputField
						showLeftIcon={true}
						name="OtherDeductions.TotalSection80CCH"
						value={
							RegimeType === RegimeTypes.NEW_TAX_REGIME
								? ""
								: values.OtherDeductions.TotalSection80CCH
						}
						onBlur={handleBlur("OtherDeductions.TotalSection80CCH")}
						onChange={(value) => {
							if (RegimeType === RegimeTypes.NEW_TAX_REGIME) {
								return;
							}
							setFieldValue("OtherDeductions.TotalSection80CCH", value);
							setFieldValue("DeductUndChapVIA.Section80CCH", Number(value));
							handleTotalCalculation("Section80CCH", Number(value));
						}}
						isInvalid={
							Boolean(getIn(errors, `OtherDeductions.TotalSection80CCH`)) &&
							getIn(touched, `OtherDeductions.TotalSection80CCH`)
						}
						errorMsg={getIn(errors, `OtherDeductions.TotalSection80CCH`)}
						isDisabled={RegimeType === RegimeTypes.NEW_TAX_REGIME}
					/>
				</FormField>
			</section>
		</>
	);
};

export default OtherDeductionsDetailsForm;
